import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { ListResponse, Load, PersonOption, Truck } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton, StatusBadge,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, PlusIcon } from '../../components/icons'

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'incoming', label: 'Incoming' },
  { value: 'outgoing', label: 'Outgoing' },
]

export default function Loads() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('all')
  const [showForm, setShowForm] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'loads', tab],
    queryFn: () => api.get<ListResponse<Load>>(`/leader/loads?tab=${tab}`),
  })
  const trucksQuery = useQuery({
    queryKey: ['leader', 'trucks'],
    queryFn: () => api.get<{ ok: boolean; items: Truck[] }>('/leader/trucks'),
    enabled: showForm,
  })
  const recipientsQuery = useQuery({
    queryKey: ['leader', 'recipients'],
    queryFn: () => api.get<{ ok: boolean; items: PersonOption[] }>('/leader/recipients'),
    enabled: showForm,
  })

  const dispatch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/leader/loads', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'loads'] })
      setShowForm(false)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const recipientUid = String(form.get('recipient_uid') ?? '')
    const recipient = recipientsQuery.data?.items.find((p) => p.uid === recipientUid)
    dispatch.mutate({
      truck_id: String(form.get('truck_id') ?? ''),
      mineral_type: String(form.get('mineral_type') ?? '').trim(),
      tonnage: Number(form.get('tonnage') ?? 0),
      source_label: String(form.get('source_label') ?? '').trim(),
      destination_label: String(form.get('destination_label') ?? '').trim(),
      expected_recipient_uids: recipientUid ? [recipientUid] : [],
      expected_recipient_names: recipient ? [recipient.name ?? recipient.email ?? ''] : [],
    })
  }

  const loads = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Loads &amp; dispatch</h1>
          <p className="page-head__sub">Track trucks in transit and confirm deliveries.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Dispatch a load'}
        </Button>
        {/* Mobile counterpart of the button above - fixed above the bottom
            nav so the primary action is always one thumb-tap away, never
            scrolls out of view. Hidden on desktop (see .fab-trigger__inline). */}
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel dispatch' : 'Dispatch a load'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Dispatch a load" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {dispatch.isError && (
                  <Alert tone="danger">
                    {dispatch.error instanceof ApiError ? dispatch.error.message : 'Could not dispatch that load.'}
                  </Alert>
                )}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Truck & cargo', content: (
                      <div className="stack">
                        <div className="grid grid--2">
                          <Select label="Truck" name="truck_id" required defaultValue="">
                            <option value="" disabled>Select a truck…</option>
                            {(trucksQuery.data?.items ?? []).map((t) => (
                              <option key={t.id} value={t.id}>{t.name} ({t.plate})</option>
                            ))}
                          </Select>
                          <Input label="Mineral" name="mineral_type" required />
                        </div>
                        <Input label="Tonnage" name="tonnage" type="number" step="any" min="0" required />
                      </div>
                    ) },
                    { title: 'Route', content: (
                      <div className="grid grid--2">
                        <Input label="Loading from" name="source_label" placeholder="e.g. Pit 3" />
                        <Input label="Destination" name="destination_label" placeholder="e.g. Chambeshi smelter" />
                      </div>
                    ) },
                    { title: 'Recipient', content: (
                      <Select label="Recipient (optional)" name="recipient_uid" defaultValue="">
                        <option value="">None</option>
                        {(recipientsQuery.data?.items ?? []).map((p) => (
                          <option key={p.uid} value={p.uid}>{p.name || p.email}</option>
                        ))}
                      </Select>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={dispatch.isPending}>Dispatch</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Loads"
          action={(
            <div className="tab-scroll" style={{ gap: 4 }}>
              {TABS.map((t) => (
                <Button key={t.value} size="sm" variant={tab === t.value ? 'primary' : 'ghost'}
                        onClick={() => setTab(t.value)}>
                  {t.label}
                </Button>
              ))}
            </div>
          )}
        />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : loads.length === 0 ? (
            <EmptyState title="No loads yet" body="Dispatched loads will appear here." />
          ) : (
            <>
              {/* PWA tap rows (5-column table clipped Status off a phone);
                  desktop keeps the table. */}
              <div className="mobile-only">
                {loads.map((l) => (
                  <Link key={l.id} to={`/loads/${l.id}`} className="lrow">
                    <div className="lrow__main">
                      <div className="lrow__title">
                        {titleCase(l.mineral_type) || 'Load'} · {formatNumber(l.tonnage, 1)} t
                      </div>
                      <div className="lrow__sub">
                        {[[l.source_label, l.destination_label].filter(Boolean).join(' → '),
                          formatDate(l.created_at)].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="lrow__end"><StatusBadge status={l.status} /></div>
                  </Link>
                ))}
              </div>
              <div className="table-wrap desktop-only">
                <table className="table">
                  <thead><tr><th>Date</th><th>Mineral</th><th className="table__num">Tonnage</th><th>Route</th><th>Status</th></tr></thead>
                  <tbody>
                    {loads.map((l) => (
                      <tr key={l.id}>
                        <td>{formatDate(l.created_at)}</td>
                        <td><Link to={`/loads/${l.id}`} style={{ fontWeight: 600 }}>{titleCase(l.mineral_type) || 'Load'}</Link></td>
                        <td className="table__num">{formatNumber(l.tonnage, 1)} t</td>
                        <td>{[l.source_label, l.destination_label].filter(Boolean).join(' → ') || '—'}</td>
                        <td><StatusBadge status={l.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}
